import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { assertEvent, assign, fromPromise, setup } from "xstate";
import { IOrganisationSwitchParams } from "./organisation-service.interface";

export enum OrganisationState {
    InitialState = "InitialState",
    OrganisationEntityUpdatedState = "OrganisationEntityUpdatedState",
    OrganisationReadyState = "OrganisationReadyState",
    SwitchingState = "SwitchingState",
    AwaitingUserInitialisationState = "AwaitingUserInitialisationState",
}

export enum OrganisationEvent {
    SetOrganisationEntity = "SetOrganisationEntity",
    UpdateOrganisationEntity = "UpdateOrganisationEntity",
    WaitForPerson = "WaitForPerson",
    Logout = "Logout",
    Initialise = "Initialise",
    DoPostInitialisation = "DoPostInitialisation",
    SwitchOrganisation = "SwitchOrganisation",
    FallbackSwitch = "FallbackSwitch",
}

export enum OrganisationActions {
    InitialiseWithOrganisationEntity = "InitialiseWithOrganisationEntity",
    NotifyOrganisationEntityUpdatedEvent = "NotifyOrganisationEntityUpdatedEvent",
    NotifyOrganisationChangingEvent = "NotifyOrganisationChangingEvent",
    NotifyOrganisationChangedEvent = "NotifyOrganisationChangedEvent",
    PromiseToPerformOrganisationSwitch = "PromiseToPerformOrganisationSwitch",
}

export type OrganisationEvents =
    | { type: OrganisationEvent.SetOrganisationEntity, organisation?: Organisation }
    | { type: OrganisationEvent.UpdateOrganisationEntity }
    | { type: OrganisationEvent.WaitForPerson }
    | { type: OrganisationEvent.Logout }
    | { type: OrganisationEvent.Initialise }
    | { type: OrganisationEvent.DoPostInitialisation }
    | { type: OrganisationEvent.SwitchOrganisation } & IOrganisationSwitchParams
    | { type: OrganisationEvent.FallbackSwitch };

// the full event body for a given event type
// e.g. OrganisationEvent.SetOrganisationEntity = { type: ..., organisation?: Organisation }
export type OrganisationEventAllParameters<T extends OrganisationEvents["type"]> = Extract<OrganisationEvents, { type: T }>;

// the parameters for the given event type, or undefined if no arguments are required.
// e.g. Logout = undefined, SetOrganisationEntity = { organisation?: Organisation }
export type OrganisationEventParameters<T extends OrganisationEvents["type"]> =
    Omit<OrganisationEventAllParameters<T>, "type"> extends infer P
        // keyof for the above will be never if there are no parameters
        ? keyof P extends never ? undefined : P
        : never;

// the action functions for the event types.
// e.g. Logout(), SetOrganisationEntity({ organisation })
export type OrganisationEventActions = {
    [Event in OrganisationEvents["type"] as Uncapitalize<Event>]:
        OrganisationEventParameters<Event> extends undefined
            ? () => void
            : (data: OrganisationEventParameters<Event>) => void;
}

export interface IOrganisationContext {
    error?: any;
    organisation?: Organisation;
}

const machineDefaultContext: IOrganisationContext = {
    organisation: undefined,
    error: undefined,
};

// Visualise at: https://stately.ai/viz/9fc5e4f3-6c20-4b83-a465-a6ad9feeb2a0
export const OrganisationMachine = setup({
    types: {
        context: {} as IOrganisationContext,
        events: {} as OrganisationEvents,
    },
    actions: {
        // we need to stub these functions for types to be correct.
        // see: https://stately.ai/blog/2024-02-02-migrating-machines-to-xstate-v5#stubbing-actions
        // ours are simple as none of these take parameters.
        /* eslint-disable @typescript-eslint/no-empty-function */
        [OrganisationActions.InitialiseWithOrganisationEntity]: () => {},
        [OrganisationActions.NotifyOrganisationEntityUpdatedEvent]: () => {},
        [OrganisationActions.NotifyOrganisationChangingEvent]: () => {},
        [OrganisationActions.NotifyOrganisationChangedEvent]: () => {},
        /* eslint-enable @typescript-eslint/no-empty-function */
    },
    actors: {
        // see: https://stately.ai/blog/2024-02-02-migrating-machines-to-xstate-v5#stubbing-actors
        [OrganisationActions.PromiseToPerformOrganisationSwitch]: fromPromise<void, IOrganisationSwitchParams>(async () => {
            throw new Error("Not implemented");
        }),
    },
}).createMachine({
    id: "OrganisationState",
    initial: OrganisationState.InitialState,
    context: () => ({ ...machineDefaultContext }),
    on: {
        [OrganisationEvent.SetOrganisationEntity]: {
            actions: assign(({ event }) => ({
                organisation: event.organisation,
                error: undefined,
            })),
        },
        [OrganisationEvent.Logout]: {
            actions: [
                assign({ ...machineDefaultContext }),
                OrganisationActions.NotifyOrganisationEntityUpdatedEvent,
            ],
            // since this is not in a state, need to say it's targeting the root states
            // (why???)
            target: `.${OrganisationState.AwaitingUserInitialisationState}`,
        },
    },
    states: {
        [OrganisationState.InitialState]: {
            entry: [
                assign({ ...machineDefaultContext }),
                OrganisationActions.InitialiseWithOrganisationEntity,
            ],
            on: {
                [OrganisationEvent.UpdateOrganisationEntity]: {
                    target: OrganisationState.OrganisationEntityUpdatedState,
                },
                [OrganisationEvent.WaitForPerson]: {
                    target: OrganisationState.AwaitingUserInitialisationState,
                },
                [OrganisationEvent.Initialise]: {
                    target: OrganisationState.InitialState,
                    // will not call initialiseWithOrganisationEntity again
                    reenter: false,
                },
            },
        },
        [OrganisationState.OrganisationEntityUpdatedState]: {
            entry: [
                // - this will be picked up by features service, which will call userService.promiseToNotifyUserChanged upon completion
                // - auth service will pick up user changed notification and notify permission completed
                OrganisationActions.NotifyOrganisationEntityUpdatedEvent,
                OrganisationActions.InitialiseWithOrganisationEntity,
            ],
            on: {
                [OrganisationEvent.DoPostInitialisation]: {
                    target: OrganisationState.OrganisationReadyState,
                },
                [OrganisationEvent.SwitchOrganisation]: {
                    target: OrganisationState.SwitchingState,
                },
            },
        },
        [OrganisationState.OrganisationReadyState]: {
            entry: [
                OrganisationActions.NotifyOrganisationChangedEvent,
            ],
            on: {
                [OrganisationEvent.DoPostInitialisation]: {
                    target: OrganisationState.OrganisationReadyState,
                },
                [OrganisationEvent.SwitchOrganisation]: {
                    target: OrganisationState.SwitchingState,
                },
            },
        },
        [OrganisationState.SwitchingState]: {
            invoke: {
                id: OrganisationActions.PromiseToPerformOrganisationSwitch,
                src: OrganisationActions.PromiseToPerformOrganisationSwitch,
                input: ({ event }) => {
                    assertEvent(event, OrganisationEvent.SwitchOrganisation);
                    return event;
                },
                onDone: {
                    actions: OrganisationActions.NotifyOrganisationChangingEvent,
                    target: OrganisationState.AwaitingUserInitialisationState,
                },
                onError: {
                    actions: assign(({ event }) => ({ error: event.error })),
                    target: OrganisationState.OrganisationReadyState,
                },
            },
        },
        [OrganisationState.AwaitingUserInitialisationState]: {
            on: {
                [OrganisationEvent.SwitchOrganisation]: {
                    target: OrganisationState.SwitchingState,
                },
                [OrganisationEvent.Initialise]: {
                    target: OrganisationState.InitialState,
                },
                [OrganisationEvent.FallbackSwitch]: {
                    target: OrganisationState.OrganisationReadyState,
                },
            },
        },
    },
});
